import { FC } from 'react'
import { useMutation, gql } from '@apollo/client'
import slugify from '@sindresorhus/slugify'
import {
  VStack,
  StackDivider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  FormHelperText,
  Code,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'

const initialValues = {
  displayName: '',
  repoUrl: '',
  defaultBranch: '',
}

const CREATE_APP_MUTATION = gql`
  mutation CreateApp(
    $name: String!
    $displayName: String!
    $repoUrl: String!
    $defaultBranch: String!
  ) {
    createApp(
      name: $name
      displayName: $displayName
      repoUrl: $repoUrl
      defaultBranch: $defaultBranch
    ) {
      id
      name
    }
  }
`

type Props = {
  refetchAppList: () => void
}

const AddApplicationModal: FC<Props> = ({ refetchAppList }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [createApp, { loading }] = useMutation(CREATE_APP_MUTATION)

  const formik = useFormik({
    initialValues,
    validate: (values) => {
      const GITHUB_URL_REGEXP = /github\.com\/(\w|-)+\/(\w|-)+/
      const errors: Record<string, string> = {}

      if (!values.displayName) {
        errors.displayName = 'Required'
      } else if (values.displayName.length < 5) {
        errors.displayName =
          'Application name must be at least 5 characters long'
      }

      if (!values.repoUrl) {
        errors.repoUrl = 'Required'
      } else if (!values.repoUrl.startsWith('https://')) {
        errors.repoUrl = "Protocol is missing, please add https:// at the beginning"
      } else if (!GITHUB_URL_REGEXP.test(values.repoUrl)) {
        errors.repoUrl = "Your GitHub URL doesn't seem to be a valid one"
      }

      if (!values.defaultBranch) {
        errors.defaultBranch = 'Required'
      }

      return errors
    },
    onSubmit: async (values) => {
      const variables = {
        ...values,
        name: slugify(values.displayName),
      }
      await createApp({ variables })
      await refetchAppList()
      onModalClose()
    },
  })

  const isControlInvalid = (controlName: keyof typeof initialValues) =>
    !!(formik.errors[controlName] && formik.touched[controlName])

  const onModalClose = () => {
    navigate('/')
    formik.resetForm()
  }

  return (
    <Modal
      isOpen={location.pathname === '/app/new'}
      size="2xl"
      onClose={onModalClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Application</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={formik.handleSubmit}>
            <VStack
              divider={<StackDivider borderColor="gray.200" />}
              spacing={4}
              align="stretch"
            >
              <FormControl
                colorScheme="cyan"
                isRequired
                isInvalid={isControlInvalid('displayName')}
              >
                <FormLabel htmlFor="displayName">Application name</FormLabel>
                <Input
                  autoFocus
                  type="text"
                  id="displayName"
                  name="displayName"
                  onChange={formik.handleChange}
                  value={formik.values.displayName}
                />
                <FormHelperText>Minimum 5 characters.</FormHelperText>
                <FormErrorMessage>{formik.errors.displayName}</FormErrorMessage>
              </FormControl>
              <FormControl
                colorScheme="cyan"
                isRequired
                isInvalid={isControlInvalid('repoUrl')}
              >
                <FormLabel htmlFor="repoUrl">GitHub URL</FormLabel>
                  <Input
                    type="text"
                    id="repoUrl"
                    name="repoUrl"
                    onChange={formik.handleChange}
                    value={formik.values.repoUrl}
                  />
                <FormHelperText>
                  Make sure the repository is a <b>public</b> one. (e.g.:
                  https://github.com/sindresorhus/p-limit)
                </FormHelperText>
                <FormErrorMessage>{formik.errors.repoUrl}</FormErrorMessage>
              </FormControl>
              <FormControl
                id="default-branch"
                colorScheme="cyan"
                isRequired
                isInvalid={isControlInvalid('defaultBranch')}
              >
                <FormLabel htmlFor="defaultBranch">Default branch</FormLabel>
                <Input
                  type="text"
                  id="defaultBranch"
                  name="defaultBranch"
                  onChange={formik.handleChange}
                  value={formik.values.defaultBranch}
                  placeholder="main"
                />
                <FormHelperText>
                  The base branch of your application. (e.g.: <Code>main</Code>
                  {' or '}
                  <Code>master</Code>)
                </FormHelperText>
                <FormErrorMessage>
                  {formik.errors.defaultBranch}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="cyan"
            variant="ghost"
            mr={3}
            onClick={onModalClose}
          >
            Close
          </Button>
          <Button
            bgColor="cyan.600"
            color="white"
            onClick={() => formik.handleSubmit()}
            isLoading={formik.isSubmitting || loading}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddApplicationModal
